import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  alterarCodigoContaService,
  listarGruposEmpresaDoUsuario,
} from '~/Services/Service';
import Loading from '~/Components/Loading';
import AuthStorage from '~/Utils/AuthStorage';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  CARACTERISTICA_HOMOLOGADOR,
  CARACTERISTICA_ALTERAR_CODIGO_CONTA,
  MSG_SUCESSO_ALTERACAO,
} from '~/constant';
import SelecionarConta from './components/SelecionarConta';
import FormAlteracao from './components/FormAlteracao';
import ModalConfirmacaoAlteracao from './components/ModalConfirmacao';
import { alterarCodigoContaSchema } from './validationSchema';

// W-0651 — Altera Código Conta (Ferramenta GES, DESTRUTIVA, IRREVERSÍVEL).
// Fonte: Ualteracodigoconta.pas. Tela única (gate-1b Q-05=A): combo GE -> busca conta -> grid ->
// seleção -> form (novo código + observação) -> validar (dry-run) -> modal HARD GATE -> executar.
const AlterarCodigoConta = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [contas, setContas] = useState([]);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [modo, setModo] = useState('codigo');
  const [busca, setBusca] = useState('');
  const [codigoGrupoEmpresa, setCodigoGrupoEmpresa] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [loading, setLoading] = useState(false);

  const possuiPermissaoAcesso =
    AuthStorage.temCaracteristica(CARACTERISTICA_HOMOLOGADOR) &&
    AuthStorage.temCaracteristica(CARACTERISTICA_ALTERAR_CODIGO_CONTA);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const res = await listarGruposEmpresaDoUsuario();
        if (!ativo) return;
        setGruposEmpresa(Array.isArray(res) ? res : []);
      } catch (err) {
        if (!ativo) return;
        setGruposEmpresa([]);
        toast.error(extrairMensagemErro(err));
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesGe = useMemo(
    () =>
      gruposEmpresa.map((ge) => ({
        value: ge.codigo,
        label: `${ge.codigo} - ${ge.apelido}`,
      })),
    [gruposEmpresa]
  );

  const geLabel = useMemo(() => {
    const opt = opcoesGe.find((o) => o.value === codigoGrupoEmpresa);
    return opt ? opt.label : '';
  }, [opcoesGe, codigoGrupoEmpresa]);

  const buscarContas = useCallback(async () => {
    if (!codigoGrupoEmpresa) return;
    try {
      setCarregandoContas(true);
      const res = await alterarCodigoContaService.listarContas(
        codigoGrupoEmpresa,
        busca,
        modo
      );
      setContas(Array.isArray(res) ? res : []);
    } catch (err) {
      setContas([]);
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregandoContas(false);
    }
  }, [codigoGrupoEmpresa, busca, modo]);

  const onSelecionarGe = (value) => {
    setCodigoGrupoEmpresa(value);
    setContas([]);
    setContaSelecionada(null);
  };

  // Submit do form -> dry-run no backend; se válido abre o modal HARD GATE.
  const handleSubmit = async (values, { setFieldError }) => {
    try {
      setLoading(true);
      const r = await alterarCodigoContaService.validar({
        codigo_grupo_empresa: Number(values.codigoGrupoEmpresa),
        codigo_conta_atual: Number(values.codigoContaAtual),
        codigo_conta_novo: Number(values.codigoContaNovo),
      });
      if (r && r.valido === false) {
        setFieldError(
          'codigoContaNovo',
          r.mensagem_erro || 'Código de conta inválido.'
        );
        return;
      }
      setPendingValues(values);
      setShowConfirm(true);
    } catch (err) {
      // Guards do backend vêm como 400 inline em codigoContaNovo (TechSpec §3.6).
      setFieldError('codigoContaNovo', extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmClose = async (confirmado, resetForm) => {
    setShowConfirm(false);
    if (!confirmado || !pendingValues) {
      setPendingValues(null);
      return;
    }
    setLoading(true);
    try {
      const resp = await alterarCodigoContaService.executar({
        codigo_grupo_empresa: Number(pendingValues.codigoGrupoEmpresa),
        codigo_conta_atual: Number(pendingValues.codigoContaAtual),
        codigo_conta_novo: Number(pendingValues.codigoContaNovo),
        observacao: pendingValues.observacao,
      });
      toast.success((resp && resp.mensagem) || MSG_SUCESSO_ALTERACAO);
      setPendingValues(null);
      setContaSelecionada(null);
      if (resetForm) resetForm();
      await buscarContas();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  if (!possuiPermissaoAcesso) {
    return (
      <div className="container-fluid mt-3">
        <Card>
          <Card.Header>
            <h4 className="mb-0">Altera Código de Conta</h4>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-0">
              Você não possui permissão para acessar essa funcionalidade.
            </p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '24px 32px',
              borderRadius: 4,
            }}
          >
            <Loading label="Processando alteração de código..." />
          </div>
        </div>
      )}

      <Card>
        <Card.Header>
          <h4 className="mb-0">Altera Código de Conta</h4>
        </Card.Header>
        <Card.Body>
          <Formik
            enableReinitialize
            initialValues={{
              codigoGrupoEmpresa: codigoGrupoEmpresa || '',
              codigoContaAtual: contaSelecionada
                ? contaSelecionada.codigo_conta
                : '',
              codigoContaNovo: '',
              observacao: '',
            }}
            validationSchema={alterarCodigoContaSchema}
            onSubmit={handleSubmit}
          >
            {({ values, resetForm, setFieldValue }) => (
              <Form>
                <SelecionarConta
                  opcoesGe={opcoesGe}
                  codigoGrupoEmpresa={codigoGrupoEmpresa}
                  onSelecionarGe={(value) => {
                    onSelecionarGe(value);
                    setFieldValue('codigoGrupoEmpresa', value || '');
                    setFieldValue('codigoContaAtual', '');
                  }}
                  modo={modo}
                  onAlterarModo={setModo}
                  busca={busca}
                  onAlterarBusca={setBusca}
                  onBuscar={buscarContas}
                  contas={contas}
                  carregando={carregandoContas}
                  contaSelecionada={contaSelecionada}
                  onSelecionarConta={(conta) => {
                    setContaSelecionada(conta);
                    setFieldValue('codigoContaAtual', conta.codigo_conta);
                  }}
                />

                <FormAlteracao
                  contaSelecionada={contaSelecionada}
                  disabled={loading}
                />

                <Row className="mt-4">
                  <Col
                    xs={12}
                    className="d-flex flex-column flex-md-row justify-content-md-end"
                  >
                    <Button
                      variant="outline-secondary"
                      className="mr-md-2 mb-2 mb-md-0"
                      onClick={() => {
                        resetForm();
                        setContaSelecionada(null);
                      }}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !contaSelecionada}
                    >
                      Alterar Código
                    </Button>
                  </Col>
                </Row>

                <ModalConfirmacaoAlteracao
                  show={showConfirm}
                  onClose={(confirmado) =>
                    handleConfirmClose(confirmado, resetForm)
                  }
                  codigoAntigo={values.codigoContaAtual}
                  codigoNovo={values.codigoContaNovo}
                  geLabel={geLabel}
                  apelidoConta={
                    contaSelecionada ? contaSelecionada.apelido_conta : ''
                  }
                  observacao={values.observacao}
                />
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AlterarCodigoConta;
